import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { Link, useLocation } from 'wouter';

import { useAuth } from '@/contexts/auth-context';
import { AppHeader } from './app-header';
import { Sidebar } from './sidebar';
import { getLicenseExpirationWarning } from './license-expiration-warning';
import { ClientMaintenanceWarning } from './client-maintenance-warning';
import {
  customerBillingQueryKey,
  customerBillingService,
} from '@/services/customer-billing.service';

export function AppLayout({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [location] = useLocation();
  const { license, user } = useAuth();
  const billing = useQuery({
    queryKey: customerBillingQueryKey,
    queryFn: customerBillingService.summary,
    enabled: user?.role === 'OWNER',
    staleTime: 30_000,
    // Gate or billing workspace owns polling; this observer shares its cache.
    refetchOnWindowFocus: true,
  });
  const graceEndsAt = billing.data?.accessBlocked === false ? billing.data.gracePeriodEndsAt : null;
  const expirationWarning = getLicenseExpirationWarning(
    billing.data?.license.expiresAt ?? license?.expiresAt,
  );
  const showExpirationWarning = user?.role === 'OWNER' && Boolean(expirationWarning || graceEndsAt);
  const trialDaysLeft = license?.expiresAt
    ? Math.max(0, Math.ceil((new Date(license.expiresAt).getTime() - Date.now()) / 86400000))
    : 0;

  return (
    <div className="min-h-screen">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <AppHeader onOpenMenu={() => setSidebarOpen(true)} />
      <main className="lg:ml-64">
        <ClientMaintenanceWarning />
        {user?.role === 'OWNER' && license?.status === 'TRIAL' && (
          <div className="border-b bg-brand-50 px-5 py-3 text-sm text-brand-950">
            <div className="mx-auto flex max-w-[1600px] flex-wrap gap-x-5 gap-y-2">
              <strong>
                Teste gratuito · {trialDaysLeft}{' '}
                {trialDaysLeft === 1 ? 'dia restante' : 'dias restantes'}
              </strong>
              <Link href="/app/primeiros-passos" className="underline">
                Primeiros passos
              </Link>
              <Link href="/financeiro/minha-assinatura" className="underline">
                Conhecer condições de contratação
              </Link>
              <span>Sem conversão paga automática.</span>
            </div>
          </div>
        )}
        {user?.impersonatedBy ? (
          <div className="border-b border-violet-300 bg-violet-950 px-4 py-3 text-sm text-white sm:px-6">
            <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-2">
              <ShieldCheck className="size-4 shrink-0" />
              <strong>Acesso assistido do Chefia Master</strong>
              <span>
                Sessão temporária e auditada. Assinaturas, pagamentos e alterações de segurança
                estão bloqueados.
              </span>
            </div>
          </div>
        ) : null}
        {showExpirationWarning ? (
          <div
            role="status"
            className={`border-b px-4 py-3 text-sm sm:px-6 ${expirationWarning?.kind === 'EXPIRED' && !graceEndsAt ? 'border-red-300 bg-red-50 text-red-950 dark:border-red-900 dark:bg-red-950/35 dark:text-red-100' : 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-100'}`}
          >
            <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-2">
              <AlertTriangle className="size-4 shrink-0" />
              <span className="font-semibold">{expirationWarning?.message}</span>
              {graceEndsAt ? (
                <span>
                  Seu acesso permanece liberado durante a tolerância, até{' '}
                  {new Intl.DateTimeFormat('pt-BR', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }).format(new Date(graceEndsAt))}
                  .
                </span>
              ) : null}
              <Link
                href="/financeiro/minha-assinatura"
                className="font-semibold underline underline-offset-2"
              >
                Ver assinatura e pagamento
              </Link>
            </div>
          </div>
        ) : null}
        <motion.div
          key={location}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          className="mx-auto w-full max-w-[1600px] p-4 sm:p-6 lg:p-8"
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
}
